// PrescriptionPDFIndian.jsx
import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path
} from '@react-pdf/renderer';
import dayjs from 'dayjs';

// Compact stethoscope SVG as data URI (kept for logo; data unchanged)
const DEFAULT_LOGO =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <g fill="none" stroke="#1f2937" stroke-width="3">
      <circle cx="48" cy="16" r="6"/>
      <path d="M48 22v10c0 9-7 16-16 16s-16-7-16-16V14"/>
      <path d="M8 8v6c0 6 4 10 10 10s10-4 10-10V8"/>
      <path d="M32 48v8h10"/>
    </g>
  </svg>`);

// Subtle shield watermark to match template style
const WATERMARK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(`
  <svg width="300" height="300" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
    <path d="M32 4 L56 12 V30 C56 44 46 54 32 60 C18 54 8 44 8 30 V12 Z" fill="#0ea5a5"/>
    <rect x="29" y="18" width="6" height="20" rx="1" fill="#ffffff"/>
    <rect x="22" y="25" width="20" height="6" rx="1" fill="#ffffff"/>
  </svg>`);

// Helpers (unchanged data usage)
const show = (v, fallback = 'N/A') => (v || v === 0 ? String(v) : fallback);
const fmt = (d) => (d ? dayjs(d).format('DD-MMM-YYYY') : 'N/A');
const ageFromDOB = (dob) => {
  if (!dob) return 'N/A';
  const now = dayjs();
  const d = dayjs(dob);
  let y = now.diff(d, 'year');
  const m = now.diff(d.add(y, 'year'), 'month');
  return `${y}y${m ? ` ${m}m` : ''}`;
};

const PrescriptionPDFIndian = ({ data = {} }) => {
  // Theme color can be provided via data.accentColor; defaults to teal like the reference
  const ACCENT = data.accentColor || '#14b8a6';  // teal
  const ACCENT_DARK = '#0f766e';
  const TINT = 'rgba(20,184,166,0.08)';

  const s = StyleSheet.create({
    page: { padding: 24, fontFamily: 'Helvetica', color: '#0f172a' },

    // Layered header band with curved cut to match template
    headerWrap: { position: 'relative', height: 96, marginBottom: 14 },
    headerBand: { position: 'absolute', top: 0, left: -24, right: -24, height: 96, backgroundColor: ACCENT },
    headerCurve: { position: 'absolute', top: 0, right: -24, width: 220, height: 96 },
    headContent: { position: 'absolute', top: 14, left: 24, right: 24, flexDirection: 'row', justifyContent: 'space-between' },

    // Left doctor block (on colored band)
    docBlock: { width: '58%' },
    docName: { fontSize: 16, fontWeight: 700, color: '#ffffff' },
    docLine: { fontSize: 10, color: '#ecfeff', marginTop: 2 },

    // Right clinic block (white area under the curve)
    clinicBlock: { width: '40%', alignItems: 'flex-end' },
    logo: { width: 34, height: 34, marginBottom: 4 },
    clinicName: { fontSize: 12, fontWeight: 700, color: '#0f172a' },
    clinicLine: { fontSize: 9, color: '#334155', marginTop: 1 },

    // Thin accent underline under header
    headUnderline: { height: 4, backgroundColor: ACCENT_DARK, marginTop: 92, marginHorizontal: -24 },

    // Meta row
    metaRow: { marginTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
    text: { fontSize: 9, lineHeight: 1.35, color: '#0f172a' },
    label: { fontSize: 9, fontWeight: 700, color: '#0f172a' },

    // Patient info card (tinted panel with dashed border)
    patientCard: {
      marginTop: 8,
      backgroundColor: TINT,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: ACCENT,
      borderRadius: 6,
      padding: 8
    },
    gridRow: { flexDirection: 'row', justifyContent: 'space-between' },
    col: { width: '49%' },

    // Section titles
    h2: { fontSize: 11, fontWeight: 700, color: '#0f172a' },

    // Rx and tables
    table: { borderWidth: 1, borderColor: '#e5e7eb', borderRadius: 4, marginTop: 4 },
    th: { flexDirection: 'row', backgroundColor: '#f9fafb', borderBottomWidth: 1, borderColor: '#e5e7eb' },
    tr: { flexDirection: 'row', borderBottomWidth: 1, borderColor: '#f3f4f6' },
    cell: { paddingVertical: 4, paddingHorizontal: 6, fontSize: 9, color: '#0f172a' },
    headCell: { fontWeight: 700, color: '#0f172a' },
    cMed: { width: '38%' },
    cDos: { width: '18%' },
    cFrq: { width: '18%' },
    cDur: { width: '14%' },
    cNote: { width: '12%' },

    // Watermark
    watermarkWrap: { position: 'absolute', top: 220, left: 0, right: 0, alignItems: 'center', opacity: 0.08 },

    // Footer contact strip
    footer: {
      marginTop: 12,
      borderTopWidth: 1,
      borderColor: '#e5e7eb',
      paddingTop: 8
    },
    contactRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
    contactLabelBox: { backgroundColor: '#374151', paddingVertical: 3, paddingHorizontal: 8, borderRadius: 3, marginRight: 8 },
    contactLabelText: { fontSize: 9, color: '#ffffff', fontWeight: 700 },
    contactItem: { fontSize: 9, color: '#334155' },

    // Spacer
    sectionGap: { marginTop: 8 }
  });

  const meds = Array.isArray(data.medications) ? data.medications : [];
  const tests = Array.isArray(data.testRecommendations) ? data.testRecommendations : [];

  return (
    <Document>
      <Page size="A4" style={s.page}>
        {/* Header band + curve */}
        <View style={s.headerWrap}>
          <View style={s.headerBand} />
          <Svg style={s.headerCurve} viewBox="0 0 220 96">
            {/* White curve cutting into the colored band (matches template corner) */}
            <Path
              d="M220,0 L220,96 L0,96 C40,86 90,64 140,36 C170,20 195,8 220,0 Z"
              fill="#ffffff"
            />
          </Svg>

          <View style={s.headContent}>
            <View style={s.docBlock}>
              <Text style={s.docName}>
                {show(data.prescribedBy || data.doctorName || 'Dr. Name')}
              </Text>
              <Text style={s.docLine}>{show(data.doctorQualification || 'M.B.B.S., M.D.')}</Text>
              <Text style={s.docLine}>Reg. No: {show(data.doctorRegNo || '—')}</Text>
              {data.clinicPhone ? (
                <Text style={s.docLine}>Ph: {show(data.clinicPhone)}</Text>
              ) : null}
            </View>

            <View style={s.clinicBlock}>
              <Image style={s.logo} src={data.logo || DEFAULT_LOGO} />
              <Text style={s.clinicName}>{show(data.clinicInformations?.clinic_Hospital_Name || 'HealthCare')}</Text>
              <Text style={s.clinicLine}>{show(data.clinicInformations?.websiteURL || 'URL Here')}</Text>
            </View>
          </View>
        </View>

        <View style={s.headUnderline} />

        {/* Meta row */}
        <View style={s.metaRow}>
          <Text style={s.text}>Prescription No: {show(data.prescriptionID)}</Text>
          <Text style={s.text}>Date: {fmt(data.prescriptionDate)}</Text>
        </View>

        {/* Patient info card */}
        <View style={s.patientCard}>
          <View style={s.gridRow}>
            <View style={s.col}>
              <Text style={s.label}>Patient Name</Text>
              <Text style={s.text}>{show(data.patientFullName)}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.label}>Patient ID</Text>
              <Text style={s.text}>{show(data.patientIDUniqueID)}</Text>
            </View>
          </View>
          <View style={[s.gridRow, s.sectionGap]}>
            <View style={s.col}>
              <Text style={s.label}>Gender</Text>
              <Text style={s.text}>{show(data.gender || 'N/A')}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.label}>DOB / Age</Text>
              <Text style={s.text}>
                {fmt(data.dateofBirth)}  •  {ageFromDOB(data.dateofBirth)}
              </Text>
            </View>
          </View>
          <View style={[s.gridRow, s.sectionGap]}>
            <View style={s.col}>
              <Text style={s.label}>Mobile</Text>
              <Text style={s.text}>{show(data.contactNumber)}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.label}>—</Text>
              <Text style={s.text}> </Text>
            </View>
          </View>
        </View>

        {/* Watermark in the center */}
        <View style={s.watermarkWrap}>
          <Image src={WATERMARK} style={{ width: 180, height: 180 }} />
        </View>

        {/* Complaints / Findings / Diagnosis */}
        <View style={s.sectionGap}>
          <View style={s.gridRow}>
            <View style={s.col}>
              <Text style={s.h2}>Chief Complaints</Text>
              <Text style={s.text}>{show(data.chiefComplaints || '—')}</Text>
            </View>
            <View style={s.col}>
              <Text style={s.h2}>Clinical Findings</Text>
              <Text style={s.text}>{show(data.clinicalFindings || '—')}</Text>
            </View>
          </View>
          <View style={s.sectionGap}>
            <Text style={s.h2}>Diagnosis</Text>
            <Text style={s.text}>{show(data.diagnosis)}</Text>
          </View>
        </View>

        {/* Rx heading */}
        <View style={[s.gridRow, { alignItems: 'center', marginTop: 8 }]}>
          <Text style={s.h2}>Rx</Text>
          <Text style={s.text}>Refills allowed as indicated per item</Text>
        </View>

        {/* Medicines table */}
        <View style={s.table}>
          <View style={s.th}>
            <Text style={[s.cell, s.headCell, s.cMed]}>Medicine</Text>
            <Text style={[s.cell, s.headCell, s.cDos]}>Dosage</Text>
            <Text style={[s.cell, s.headCell, s.cFrq]}>Frequency</Text>
            <Text style={[s.cell, s.headCell, s.cDur]}>Duration</Text>
            <Text style={[s.cell, s.headCell, s.cNote]}>Notes</Text>
          </View>
          {(!Array.isArray(data.medications) || data.medications.length === 0) ? (
            <View style={s.tr}>
              <Text style={[s.cell, { width: '100%' }]}>No medicines added</Text>
            </View>
          ) : (
            data.medications.map((m, i) => (
              <View key={m.medicationID || i} style={s.tr}>
                <Text style={[s.cell, s.cMed]}>{show(m.medicationName)}</Text>
                <Text style={[s.cell, s.cDos]}>{show(m.dosage || m.strength || '—')}</Text>
                <Text style={[s.cell, s.cFrq]}>{show(m.frequency || '—')}</Text>
                <Text style={[s.cell, s.cDur]}>
                  {show(m.duration || '—')} {show(m.durationUnit || '')}
                </Text>
                <Text style={[s.cell, s.cNote]}>
                  {m.allowRefills ? `Refill:${show(m.numberOfRefills)}` : 'No refill'}
                </Text>
              </View>
            ))
          )}
        </View>

        {/* Advice and tests */}
        <View style={s.sectionGap}>
          <Text style={s.h2}>Advice</Text>
          <Text style={s.text}>{show(data.advice || data.generalAdvice || '—')}</Text>
        </View>

        <View style={s.sectionGap}>
          <Text style={s.h2}>Tests Recommended</Text>
          <Text style={s.text}>
            {Array.isArray(data.testRecommendations) && data.testRecommendations.length
              ? data.testRecommendations.map((t) => t.testName).join(', ')
              : '—'}
          </Text>
        </View>

        {/* Notes for Pharmacist */}
        {data.notesForPharmacist ? (
          <View style={s.sectionGap}>
            <Text style={s.h2}>Notes for Pharmacist</Text>
            <Text style={s.text}>{show(data.notesForPharmacist)}</Text>
          </View>
        ) : null}

        {/* Follow-up and signature */}
        <View style={[s.gridRow, { marginTop: 10 }]}>
          <View style={{ width: '55%' }}>
            <Text style={s.text}>Follow‑up: {fmt(data.followUpDate)}</Text>
            <Text style={s.text}>Expiry: {fmt(data.expiryDate)}</Text>
            <Text style={s.text}>Type: {show(data.prescriptionType)}</Text>
          </View>
          <View style={{ width: '40%', alignItems: 'flex-end' }}>
            <Text style={s.text}>__________________________</Text>
            <Text style={s.text}>Authorized Signatory</Text>
          </View>
        </View>

        {/* Footer contact strip to match template */}
        <View style={s.footer}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
            <View style={s.contactLabelBox}>
              <Text style={s.contactLabelText}>Contact:</Text>
            </View>
            <View style={{ height: 1, backgroundColor: '#e5e7eb', flex: 1 }} />
          </View>
          <View style={s.contactRow}>
            <Text style={s.contactItem}>Phone: {show(data.clinicPhone || '—')}</Text>
            <Text style={s.contactItem}>Email: {show(data.clinicEmail || '—')}</Text>
            <Text style={s.contactItem}>Website: {show(data.clinicWebsite || '—')}</Text>
            <Text style={s.contactItem}>Address: {show(data.clinicAddress || '—')}</Text>
          </View>
          <View style={{ marginTop: 6, height: 4, backgroundColor: ACCENT }} />
        </View>
      </Page>
    </Document>
  );
};

export default PrescriptionPDFIndian;
